import React, { useState } from 'react';
import './Teams.css';

const Teams = ({
  teams,
  allUsers,
  teamSearch,
  teamsLoading,
  showLogos,
  uploadingTeam,
  creatingTeam,
  deletingTeamId,
  showCreateTeam,
  teamFormId,
  teamFormName,
  teamFormOwner,
  teamMembersById,
  roleOptions,
  roleEdits,
  ownerUpdatingTeam,
  removingUserId,
  addingUserTeamId,
  onSearchChange,
  onToggleLogos,
  onToggleCreateForm,
  onReload,
  onUploadLogo,
  onCreateFieldChange,
  onCreateTeam,
  onDeleteTeam,
  onRoleSelect,
  onRoleApply,
  onSetOwner,
  onAddUser,
  onRemoveUser,
  formatTeamName,
  formatMembers,
  formatOwner,
  statusMsg,
}) => {
  const [openTeams, setOpenTeams] = useState({});
  const [addInputs, setAddInputs] = useState({});

  const toggleTeam = (teamId) => {
    setOpenTeams((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  const onChangeAddInput = (teamId, value) => {
    setAddInputs((prev) => ({ ...prev, [teamId]: value }));
  };

  const filterSuggestions = (teamId) => {
    const term = (addInputs[teamId] || '').trim().toLowerCase();
    if (!term) return [];
    const currentIds = new Set((teamMembersById?.[teamId] || []).map((u) => u.id));
    return allUsers
      .filter((u) => {
        if (currentIds.has(u.id)) return false;
        const email = (u.email || '').toLowerCase();
        const ign = (u.ign || '').toLowerCase();
        return email.includes(term) || ign.includes(term);
      })
      .slice(0, 5);
  };

  return (
    <div className="sa-card">
      <div className="sa-card-header">
        <h2>Teams</h2>
        <div className="sa-actions">
          <input
            className="sa-input"
            placeholder="Search team name"
            value={teamSearch}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <button className="sa-btn ghost" onClick={onToggleLogos}>
            {showLogos ? 'Hide logos' : 'Show logos'}
          </button>
          <button className="sa-btn ghost" onClick={onReload} disabled={teamsLoading}>
            {teamsLoading ? 'Loading...' : 'Reload'}
          </button>
          <button className="sa-btn" onClick={onToggleCreateForm}>
            {showCreateTeam ? 'Close create' : 'Create team'}
          </button>
        </div>
      </div>
      {showCreateTeam && (
        <div className="sa-team-form sa-inline">
          <input
            className="sa-input"
            placeholder="Team ID (optional)"
            value={teamFormId}
            onChange={(e) => onCreateFieldChange('id', e.target.value)}
          />
          <input
            className="sa-input"
            placeholder="Team name"
            value={teamFormName}
            onChange={(e) => onCreateFieldChange('name', e.target.value)}
          />
          <input
            className="sa-input"
            placeholder="Owner (optional)"
            value={teamFormOwner}
            onChange={(e) => onCreateFieldChange('owner', e.target.value)}
          />
          <button className="sa-btn" onClick={onCreateTeam} disabled={creatingTeam}>
            {creatingTeam ? 'Creating...' : 'Create team'}
          </button>
        </div>
      )}
      {statusMsg && <div className="sa-status">{statusMsg}</div>}
      <div className="sa-table">
        <div className="sa-table-head">
          <span>Team</span>
          <span>Members</span>
          <span>Owner</span>
          <span>Actions</span>
        </div>
        {teams.map((t) => (
          <div className="sa-table-row" key={t.id}>
            <div className="sa-team-row-top">
              <span className="sa-team-cell">
                <button className="sa-link" onClick={() => toggleTeam(t.id)}>
                  {openTeams[t.id] ? '▼' : '▶'}
                </button>
                {formatTeamName(t)}
                {showLogos && t.logo && <img src={t.logo} alt="logo" className="sa-logo-thumb" />}
              </span>
              <span>{formatMembers(t)}</span>
              <span>{formatOwner(t)}</span>
              <span className="sa-row-actions">
                <div className="sa-inline">
                  <button
                    className="sa-link"
                    onClick={() => {
                      const input = document.getElementById(`logo-${t.id}`);
                      if (input) input.click();
                    }}
                    disabled={uploadingTeam === t.id}
                  >
                    {uploadingTeam === t.id ? 'Uploading...' : 'Upload logo'}
                  </button>
                  <input
                    id={`logo-${t.id}`}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => onUploadLogo(t.id, e.target.files?.[0] || null)}
                  />
                  {t.logo && <span className="sa-chip">Has logo</span>}
                  <button
                    className="sa-link danger"
                    onClick={() => onDeleteTeam(t.id)}
                    disabled={deletingTeamId === t.id}
                  >
                    {deletingTeamId === t.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </span>
            </div>

            {openTeams[t.id] && (
              <div className="sa-team-roster">
                <div className="sa-roster-head">
                  <span>Email</span>
                  <span>IGN</span>
                  <span>Role</span>
                  <span>Owner</span>
                </div>
                {(teamMembersById?.[t.id] || []).map((u) => {
                  const isOwner = t.owner === u.id || u.isOwner;
                  return (
                    <div className="sa-roster-row" key={u.id}>
                      <span>{u.email || '-'}</span>
                      <span>{u.ign || '-'}</span>
                      <span className="sa-inline">
                        <select
                          className="sa-select"
                          value={roleEdits[u.id] || u.role || ''}
                          onChange={(e) => onRoleSelect(u.id, e.target.value)}
                        >
                          <option value="">Select role</option>
                          {roleOptions.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                        <button className="sa-link" onClick={() => onRoleApply(u.id)}>
                          Apply
                        </button>
                      </span>
                  <span>
                    {isOwner ? (
                      <span className="sa-chip">Owner</span>
                    ) : (
                      <button
                            className="sa-link"
                            onClick={() => onSetOwner(t.id, u.id)}
                          disabled={ownerUpdatingTeam === t.id}
                        >
                          {ownerUpdatingTeam === t.id ? 'Setting...' : 'Make owner'}
                        </button>
                      )}
                      <button
                        className="sa-link danger"
                        onClick={() => onRemoveUser(t.id, u.id)}
                        disabled={removingUserId === u.id}
                      >
                        {removingUserId === u.id ? 'Removing...' : 'Remove'}
                      </button>
                    </span>
                  </div>
                );
              })}
                {(teamMembersById?.[t.id] || []).length === 0 && (
                  <div className="sa-empty">No users in this team.</div>
                )}
                <div className="sa-roster-add">
                  <div className="sa-inline sa-add-row">
                    <button
                      className="sa-link"
                      onClick={() => {
                        setOpenTeams((prev) => ({ ...prev, [t.id]: true }));
                        onChangeAddInput(t.id, '');
                      }}
                      disabled={addingUserTeamId === t.id}
                    >
                      + Add another user
                    </button>
                    <input
                      className="sa-input sa-add-input"
                      placeholder="Type email or IGN"
                      value={addInputs[t.id] || ''}
                      onChange={(e) => onChangeAddInput(t.id, e.target.value)}
                      disabled={addingUserTeamId === t.id}
                    />
                  </div>
                  <div className="sa-suggestions">
                    {filterSuggestions(t.id).map((u) => (
                      <button
                        key={u.id}
                        className="sa-suggestion"
                        onClick={() => onAddUser(t.id, u.id)}
                        disabled={addingUserTeamId === t.id}
                      >
                        <span className="sa-suggestion-main">{u.email || u.id}</span>
                        <span className="sa-suggestion-sub">
                          {(u.firstName || '') + ' ' + (u.lastName || '')} {u.ign ? `(${u.ign})` : ''}
                          {u.role ? ` • ${u.role}` : ''}
                        </span>
                      </button>
                    ))}
                    {filterSuggestions(t.id).length === 0 && (addInputs[t.id] || '').trim() !== '' && (
                      <div className="sa-empty">No matches.</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {teams.length === 0 && <div className="sa-empty">No teams found.</div>}
      </div>
    </div>
  );
};

export default Teams;
